import type { BucketAggregate } from "verdi-common/schemas/time-series/data-stream-query";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";

/** Ephemeral generator output — id, display name, unitCategory, aggregate. See ARCHITECTURE.md § Spec Type Flow. */
export interface ResolvedStream {
    id: number;
    unitCategory: string;
    name: string;
    /**
     * The sensor cluster's friendly name, e.g. "TreeTag". Two streams off one device in one unit
     * category are identical without it. Null when no fragment names one.
     */
    sensorClusterName: string | null;
    /**
     * The sensor element's name, e.g. "Wind Gust". Two channels on one cluster can land in the same
     * unit category, and this is all that tells them apart. Null when no fragment names one.
     */
    sensorElementName: string | null;
    /**
     * How this stream's buckets are read: its `preferredBucketAggregate`, with the API's "no
     * preference" (`null`) resolved to `mean` here so nothing downstream repeats the fallback.
     */
    aggregate: BucketAggregate;
}

/** Hide ePlant TreeTag inclination (lean) channels for now — dropped on every graph, not just the zone view. */
const EXCLUDED_SENSOR_ELEMENT_NAMES = new Set(["Inclination Angle", "Inclination Direction"]);

// Map API streams to ResolvedStream — host label from list-API hostName, otherwise "Stream {id}".
// unitCategory is passed through for panel grouping; the aggregate decides how buckets are fetched.
export function resolveStreams(streams: ListDataStreams200Item[]): ResolvedStream[] {
    return streams
        .filter((stream) => !EXCLUDED_SENSOR_ELEMENT_NAMES.has(stream.sensorElementName ?? ""))
        .map((stream) => ({
            id: stream.id,
            name: stream.hostName ?? `Stream ${stream.id}`,
            sensorClusterName: stream.sensorClusterName,
            sensorElementName: stream.sensorElementName,
            unitCategory: stream.unitCategory,
            aggregate: stream.preferredBucketAggregate ?? "mean",
        }));
}
